import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { DataGridTable, Toast as toast, Blade } from 'components'
import { GridRowSelectionModel } from '@mui/x-data-grid'
import { IAvailableELD } from 'interfaces/IEldVisibility'
import { availableEldColumns } from '../eldListTableData'
import strings from 'strings'
import { getAvailableEldList, addEldVisibility } from 'api'
import { reportException } from 'tools/logs'
import { Grid } from '@mui/material'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const t = strings.ELD_VISIBILITY

const AvailableElds: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [addingElds, setAddingElds] = useState(false)
  const [selectedElds, setSelectedElds] = useState<GridRowSelectionModel>([])
  const [availableElds, setAvailableElds] = useState<IAvailableELD[]>([])

  const fetchAvailableElds = useCallback(() => {
    setLoading(true)
    getAvailableEldList()
      .then((response) => {
        if (response.status === 200) {
          setAvailableElds(response.data ?? [])
        }
      })
      .catch((error) => {
        reportException(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (isOpen) {
      fetchAvailableElds()
    } else {
      setAvailableElds([])
      setSelectedElds([])
      setAddingElds(false)
    }
  }, [isOpen, fetchAvailableElds])

  const handleSelectionChange = useCallback((newSelection: GridRowSelectionModel) => {
    if (loading || addingElds) return
    setSelectedElds(newSelection)
  }, [loading, addingElds])

  const handleAddElds = useCallback(() => {
    if (selectedElds.length === 0) {
      toast({
        id: 'noEldSelected',
        type: 'error',
        subTitle: 'Please select at least one Telematics provider',
      })
      return
    }

    setAddingElds(true)
    addEldVisibility(selectedElds as number[])
      .then((response) => {
        if (response?.status === 200) {
          toast({
            id: 'addEldSuccess',
            type: 'success',
            subTitle: response?.message,
          })
          onSuccess()
        }
      })
      .catch((error) => {
        reportException(error)
      })
      .finally(() => {
        setAddingElds(false)
      })
  }, [selectedElds, onSuccess])

  const footerBtns = useMemo(() => ({
    primary: {
      id: 'addEldProviders',
      label: t.ADD_ELD,
      onClick: handleAddElds,
      loading: addingElds,
      disabled: addingElds || selectedElds.length === 0
    },
    secondary: {
      id: 'closeTagCreationOrUpdateBlade',
      label: 'Close',
      variant: 'outlined' as const,
      onClick: onClose
    }
  }), [handleAddElds, addingElds, selectedElds.length, onClose])

  const tableContent = useMemo(() => (
    <Grid container mt={1} mb={1} display={'flex'} sx={{ width: '100%' }}>
      <Grid item xs={12}>
        <DataGridTable
          showTitle={false}
          columns={availableEldColumns.columns}
          data={availableElds}
          loading={loading}
          checkboxSelection
          onSelectionModelChange={handleSelectionChange}
          customSelectionModel={selectedElds}
          columnVisibilityModel={{ id: false, displayName: false }}
          totalCount={availableElds.length}
          multiSearchFilter={false}
          getRowId={(row: any) => row?.id}
          reduceHeight={200}
          sortOrder={'asc'}
          sortField={'name'}
        />
      </Grid>
    </Grid>

  ), [availableElds, loading, handleSelectionChange, selectedElds])

  return (
    <Blade
      open={isOpen}
      onClose={onClose}
      title={t.AVAILABLE_ELDS}
      size="md"
      bodyNode={tableContent}
      footerBtns={footerBtns}
    />
  )
}

export default AvailableElds 